// src/contexts/AuthContext.js
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { bugsnagClient } from "../bugsnag";
import {
  ANALYTICS_EVENTS,
  identifyUser,
  resetUser,
  trackEvent,
} from "../utils/analytics";
import api from "../utils/api";
import { AbilityProvider, buildAbility } from "common/access";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (credentials) => {
    const data = await api.login(credentials);
    localStorage.setItem("token", data.token);
    if (data.refreshToken) {
      localStorage.setItem("refreshToken", data.refreshToken);
    }
    setIsAuthenticated(true);
    await validateAuth();

    // Track login event
    trackEvent(ANALYTICS_EVENTS.USER_LOGGED_IN, {
      method: "email",
    });

    return data;
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const logout = () => {
    // Track logout event before resetting
    trackEvent(ANALYTICS_EVENTS.USER_LOGGED_OUT);

    // Reset Amplitude user identity
    resetUser();

    clearSession();
    // Force redirect to login page
    window.location.href = "/login";
  };

  const validateAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const userData = await api.fetchUser();
      setUser(userData);
      setIsAuthenticated(true);

      // Identify user in Amplitude
      if (userData) {
        identifyUser({
          userId: userData._id || userData.id,
          userProperties: {
            email: userData.email,
            name: userData.name,
            role: userData.role,
            organizationId: userData.organizationId,
            organizationType: userData.organizationType,
            organizationName: userData.organizationName,
            isGlobalAdmin: userData.isGlobalAdmin || false,
          },
        });
      }
    } catch (error) {
      console.error("Token validation error:", error);
      clearSession();
      bugsnagClient.notify(error);
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const ability = useMemo(() => buildAbility(user?.rules), [user]);

  useEffect(() => {
    validateAuth();
  }, [validateAuth]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        clearSession,
        refetchUser: validateAuth,
      }}
    >
      <AbilityProvider value={ability}>{children}</AbilityProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
